import React, { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { Button, Badge } from 'src/ui/components';

import { ActivityLogListType } from '../types/activityLogType';

const MultipleAccountsPopover = ({
  activityLog,
}: {
  activityLog: ActivityLogListType;
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const observer = new IntersectionObserver(entries => {
      if (entries[0] && !entries[0].isIntersecting) setOpen(false);
    });

    if (triggerRef.current) observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [open]);

  const accounts = Array.from(new Set(activityLog?.AccountCIDPID?.split(',')));

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          ref={triggerRef}
          variant="subtle"
          size="small"
          className="-ml-2"
        >
          <InformationCircleIcon
            className="-ml-0.5 h-5 w-5 text-primary"
            aria-hidden="true"
          />
          Multiple
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          collisionPadding={20}
          className="z-10 min-w-72 max-w-[410px] border border-gray-400 bg-white text-sm shadow-xl dark:border-transparent dark:bg-neutral-700"
        >
          <div>
            <div className="flex items-center justify-between gap-2 border-b border-gray-300 px-3 py-2 dark:border-neutral-600">
              <h4 className="flex grow items-center gap-2 text-lg font-semibold">
                Accounts
                <Badge variant="info">{accounts.length}</Badge>
              </h4>
              <Popover.Close asChild>
                <Button variant="subtle" size="small">
                  <XMarkIcon className="size-4" />
                </Button>
              </Popover.Close>
            </div>
            <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto dark:divide-neutral-600/50">
              {accounts.map((acc, index) => (
                <p
                  key={index}
                  className="whitespace-nowrap px-3 py-1.5 text-sm font-semibold leading-relaxed"
                >
                  {acc}
                </p>
              ))}
            </div>
          </div>
          <Popover.Arrow className="dark:fill-neutral-700" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export { MultipleAccountsPopover };
